/**
 * Companies API Service
 *
 * Provides API functions for company/contacts management.
 */

import { apiClient } from '@/services/api';
import type {
  CompaniesListParams,
  CompaniesListResponse,
  Company,
  CompanyCategory,
  CompanyDefaultFee,
  CompanyEmployee,
  CompanyNote,
  CreateCompanyInput,
  CreateDefaultFeeInput,
  CreateEmployeeInput,
  CreateNoteInput,
  UpdateCompanyInput,
  UpdateDefaultFeeInput,
  UpdateEmployeeInput,
} from '../types';

/**
 * Companies API functions
 */
export const companiesApi = {
  // ===========================================================================
  // Categories
  // ===========================================================================

  /**
   * List all company categories
   */
  listCategories: async (): Promise<CompanyCategory[]> => {
    return apiClient.get<CompanyCategory[]>('/companies/categories');
  },

  /**
   * Get a category by ID
   */
  getCategoryById: async (id: string): Promise<CompanyCategory> => {
    return apiClient.get<CompanyCategory>(`/companies/categories/${id}`);
  },

  // ===========================================================================
  // Companies
  // ===========================================================================

  /**
   * List companies with optional filters
   */
  list: async (params?: CompaniesListParams): Promise<CompaniesListResponse> => {
    const searchParams = new URLSearchParams();

    if (params?.category_id) searchParams.set('category_id', params.category_id);
    if (params?.group_name) searchParams.set('group_name', params.group_name);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.tags?.length) searchParams.set('tags', params.tags.join(','));
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.contact_type) searchParams.set('contact_type', params.contact_type.toString());

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/companies?${queryString}` : '/companies';

    return apiClient.get<CompaniesListResponse>(endpoint);
  },

  /**
   * Get a single company by ID
   */
  getById: async (id: string): Promise<Company> => {
    return apiClient.get<Company>(`/companies/${id}`);
  },

  /**
   * Create a new company
   */
  create: async (data: CreateCompanyInput): Promise<Company> => {
    return apiClient.post<Company>('/companies', data);
  },

  /**
   * Update a company
   */
  update: async (id: string, data: UpdateCompanyInput): Promise<Company> => {
    return apiClient.put<Company>(`/companies/${id}`, data);
  },

  /**
   * Delete (soft-delete) a company
   */
  delete: async (id: string): Promise<void> => {
    return apiClient.delete<void>(`/companies/${id}`);
  },

  /**
   * Search companies by name
   */
  search: async (query: string, limit = 10): Promise<Company[]> => {
    const searchParams = new URLSearchParams();
    searchParams.set('q', query);
    searchParams.set('limit', limit.toString());
    return apiClient.get<Company[]>(`/companies/search?${searchParams.toString()}`);
  },

  // ===========================================================================
  // Employees
  // ===========================================================================

  /**
   * List employees for a company
   */
  listEmployees: async (companyId: string): Promise<CompanyEmployee[]> => {
    return apiClient.get<CompanyEmployee[]>(`/companies/${companyId}/employees`);
  },

  /**
   * Create an employee
   */
  createEmployee: async (
    companyId: string,
    data: CreateEmployeeInput,
  ): Promise<CompanyEmployee> => {
    return apiClient.post<CompanyEmployee>(`/companies/${companyId}/employees`, data);
  },

  /**
   * Update an employee
   */
  updateEmployee: async (
    companyId: string,
    employeeId: string,
    data: UpdateEmployeeInput,
  ): Promise<CompanyEmployee> => {
    return apiClient.put<CompanyEmployee>(`/companies/${companyId}/employees/${employeeId}`, data);
  },

  /**
   * Delete an employee
   */
  deleteEmployee: async (companyId: string, employeeId: string): Promise<void> => {
    return apiClient.delete<void>(`/companies/${companyId}/employees/${employeeId}`);
  },

  // ===========================================================================
  // Notes
  // ===========================================================================

  /**
   * List notes for a company
   */
  listNotes: async (companyId: string): Promise<CompanyNote[]> => {
    return apiClient.get<CompanyNote[]>(`/companies/${companyId}/notes`);
  },

  /**
   * Create a note
   */
  createNote: async (companyId: string, data: CreateNoteInput): Promise<CompanyNote> => {
    return apiClient.post<CompanyNote>(`/companies/${companyId}/notes`, data);
  },

  /**
   * Delete a note
   */
  deleteNote: async (companyId: string, noteId: string): Promise<void> => {
    return apiClient.delete<void>(`/companies/${companyId}/notes/${noteId}`);
  },

  // ===========================================================================
  // Default Fees
  // ===========================================================================

  /**
   * List default fees for a company
   */
  listDefaultFees: async (companyId: string): Promise<CompanyDefaultFee[]> => {
    return apiClient.get<CompanyDefaultFee[]>(`/companies/${companyId}/default-fees`);
  },

  /**
   * Create a default fee
   */
  createDefaultFee: async (
    companyId: string,
    data: CreateDefaultFeeInput,
  ): Promise<CompanyDefaultFee> => {
    return apiClient.post<CompanyDefaultFee>(`/companies/${companyId}/default-fees`, data);
  },

  /**
   * Update a default fee
   */
  updateDefaultFee: async (
    companyId: string,
    feeId: string,
    data: UpdateDefaultFeeInput,
  ): Promise<CompanyDefaultFee> => {
    return apiClient.put<CompanyDefaultFee>(`/companies/${companyId}/default-fees/${feeId}`, data);
  },

  /**
   * Delete a default fee
   */
  deleteDefaultFee: async (companyId: string, feeId: string): Promise<void> => {
    return apiClient.delete<void>(`/companies/${companyId}/default-fees/${feeId}`);
  },
};

export default companiesApi;
